import React, { useCallback, useEffect, useState, useRef, MouseEvent } from "react";
import cn from "classnames";
import { debounce, isNull } from "lodash";
import Icon from "../../brand/Icon";
import { SearchProps } from "./Search.props";
import styles from "./Search.module.scss";
import SearchSuggestions from "./SearchSuggestion";
import Button from "../Button";

export type { Suggestion } from "./SearchSuggestion/SearchSuggestions.props";

/**
 * @deprecated
 */
export function Search<SuggestionData>({
  id,
  testId,
  placeholder,
  name,
  onChange,
  searchBarWrapperClassName,
  containerClassName,
  inputClassName,
  disabled,
  onSuggestionClick,
  getSuggestions,
  suggestions = [],
  textLength = 3,
  onEnterPreventDefault = false,
  onEnterSearch,
  suggestionBoxHint = "No Suggestions, please revise search or enter more characters",
  autoCompleteClassName,
  loading = false,
  buttonContent = "Search",
  searchBarButtonClass,
  searchBarButtonSize = "small",
  displaySearchButton = false,
  searchBarMagnifyingGlassClass,
  suggestionTitle = "Suggestions",
  clearAfterSelected = false,
  autoFocus = false,
  buttonTestId,
  onClearSearch,
  defaultValue = "",
}: SearchProps<SuggestionData>): React.ReactElement {
  const [text, setText] = useState(defaultValue); // the text in the search
  const [open, setOpen] = useState(false); // is the dropdown open or not
  const [listFocusTarget, setListFocusTarget] = useState<number | null>(null); // if the list is visible, this is the index of the row focused on
  const inputRef = useRef<HTMLInputElement | null>(null); // reference to the search (text) input field
  const closeRef = useRef<HTMLInputElement | null>(null); // reference to the X close button in the search
  const [closeFocused, setCloseFocused] = useState(false); // true if X has focus
  const [inputFocused, setInputFocused] = useState(false); // true if input is focused

  // reset the component on refresh
  useEffect(() => {
    setText(defaultValue);
    setOpen(false);
  }, []);

  // Move cursor to end of input
  useEffect(() => {
    if (inputFocused) {
      if (!isNull(inputRef.current)) {
        inputRef.current.selectionStart = inputRef.current.value.length;
        inputRef.current.selectionEnd = inputRef.current.value.length;
      }
    }
  }, [inputFocused]);

  // debounce handleOnChange function
  const debouncedHandleOnChange = useCallback(
    debounce((e) => {
      if (typeof getSuggestions !== "undefined") {
        getSuggestions(e);
      }
    }, 500),
    [getSuggestions],
  );


  const handleOnClick = useCallback(
    (suggestion: SuggestionData) => {
      if (typeof onSuggestionClick !== "undefined") {
        onSuggestionClick(suggestion);
        if (clearAfterSelected) {
          clearSearch();
        }
      }
    },
    [onSuggestionClick],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setText(e.target.value);
      if (e.target.value.length >= textLength) {
        setOpen(true);
        debouncedHandleOnChange(e);
      } else {
        setOpen(false);
      }
      // execute onChange custom effect if there is one
      onChange && onChange(e);
    },
    [debouncedHandleOnChange, onChange, setOpen, setText, text],
  );

  const closeSuggestions = (): void => {
    setOpen(false);
  };

  const clearSearch = (): void => {
    closeSuggestions();
    setText("");
    onClearSearch && onClearSearch();
  };

  const handleSuggestionClick = (text: string, data: SuggestionData | undefined): void => {
    if (!clearAfterSelected) {
      setText(text);
    }
    if (data) handleOnClick(data);
    else handleOnClick(text as SuggestionData);

    if (inputRef !== null) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }

    closeSuggestions();
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (onEnterSearch && e.key === "Enter") {
      if (onEnterPreventDefault) {
        e.preventDefault();
      }
      onEnterSearch(text.trim());
      setOpen(false);
    }
  };

  const handleButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (onEnterSearch) {
      if (onEnterPreventDefault) {
        e.preventDefault();
      }
      onEnterSearch(text.trim());
      setOpen(false);
    }
  };

  // reset the form by: clicking X, hitting ESC, tabbing over to X then hitting space or enter
  const startOver = () => {
    clearSearch();
    if (inputRef !== null) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
    setCloseFocused(false);
    setInputFocused(true);
  };

  const handleFocusedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { nextSibling, previousSibling } = e.target as HTMLElement;
    switch (e.key) {
      case "Tab":
        if (closeFocused) {
          // if the X is focused, close the list and set closeFocused to false, and let the focus go to the next element
          setCloseFocused(false);
          setOpen(false);
          break;
        } else if (inputFocused) {
          // if the input is focused, switch focus from input to the X, DONT let focus pass to next element
          if (closeRef !== null && closeRef.current) {
            closeRef.current.focus();
            e.preventDefault();
            setCloseFocused(true);
            setInputFocused(false);
          }
        } else {
          // otherwise, we were in the list, so hitting tab from there closes the list and lets focus go to the next element
          setCloseFocused(false);
          setInputFocused(false);
          setOpen(false);
        }
        break;

      case "ArrowDown":
        if (open) {
          if (inputFocused) {
            // go to 1st element in suggestions
            if (document.querySelectorAll(".displayedSuggestions")[0]) {
              if (document.querySelectorAll(".displayedSuggestions")[0].children[0]) {
                (document.querySelectorAll(".displayedSuggestions")[0].children[0] as HTMLElement).focus();
                setInputFocused(false);
                setListFocusTarget(0);
              }
            }
          } else {
            if (nextSibling) {
              (nextSibling as HTMLElement).focus();
              setListFocusTarget((prev) => {
                if (!isNull(prev)) {
                  return prev + 1;
                }
                return null;
              });
            }
          }
        }
        break;

      case "ArrowUp":
        if (open) {
          if (previousSibling) {
            (previousSibling as HTMLElement).focus();
            setListFocusTarget((prev) => {
              if (!isNull(prev)) {
                return prev - 1;
              }
              return null;
            });
          } else {
            setInputFocused(true);
            if (inputRef !== null) {
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }
          }
        }
        e.preventDefault();
        break;

      case "Escape":
        if (open) {
          setOpen(false);
          setListFocusTarget(null);
          if (inputRef !== null) {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }
          setCloseFocused(false);
          setInputFocused(true);
        }
        break;

      case " ": // space bar
        if (closeFocused) {
          startOver();
          e.preventDefault(); // stops the input from recieving the space too
        }
        break;

      case "Enter":
        if (closeFocused) {
          startOver();
          setListFocusTarget(null);
        }
        if (!isNull(listFocusTarget) && text.trim() !== "") {
          handleSuggestionClick(suggestions[listFocusTarget].suggestionLabel, suggestions[listFocusTarget].data);
        }
        break;

      default:
    }
  };

  const searchBarContainerClasses = cn(styles["searchBar-container"], { [styles["disabled"]]: disabled }, containerClassName, {
    [styles["searchBar-is-open"]]: open,
  });
  const searchBarInputClasses = cn(styles["searchBar-input"], { [styles["open"]]: open }, inputClassName);
  const clearSuggestionClasses = cn(styles["searchBar-x"], styles["svg-x"]);
  const searchBarWrapperClasses = cn(styles["searchBarContainerWrapper"], searchBarWrapperClassName);

  let searchSuggestionsComponent;

  if (open) {
    searchSuggestionsComponent = (
      <SearchSuggestions
        suggestions={suggestions}
        id={id}
        testId={testId}
        handleSuggestionClick={handleSuggestionClick}
        text={text}
        autoCompleteClassName={autoCompleteClassName}
        suggestionBoxHint={suggestionBoxHint}
        loading={loading}
        suggestionTitle={suggestionTitle}
      />
    );
  }

  // if text is in the input, then this will render the X to clear it
  const createClosingX = () => {
    if ((text && !disabled && !displaySearchButton) || open) {
      return (
        <span
          onClick={clearSearch}
          className={clearSuggestionClasses}
          tabIndex={-1}
          role="button"
          aria-label="Clear search"
          onFocus={() => {
            setCloseFocused(true);
          }}
          onBlur={() => {
            setCloseFocused(false);
          }}
          ref={closeRef}
        >
          <Icon id={`x-${id}`} name="x" size="small" className={styles["select-x"]} />
        </span>
      );
    } else {
      return null;
    }
  };

  const searchButton = () => {
    return (
      <Button
        data-testid={testId ? `test__search-button-${testId}` : undefined}
        id={id ? `search-button-${id}` : undefined}
        testId={buttonTestId}
        size={searchBarButtonSize}
        onClick={handleButtonClick}
        className={searchBarButtonClass}
      >
        {buttonContent}
      </Button>
    );
  };

  const magnifyingGlassClass = cn(
    styles["magnifying-glass"],
    { [styles["magnifying-glass-focus"]]: inputFocused },
    searchBarMagnifyingGlassClass,
  );

  return (
    <div className={searchBarWrapperClasses}>
      <div
        className={searchBarContainerClasses}
        data-testid={testId ? `test__${testId}` : undefined}
        onClick={closeSuggestions}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleFocusedKeyDown(e)}
      >
        <Icon id={`magnifying-glass-${id}`} name="magnifying-glass" className={magnifyingGlassClass} ariaHidden="true" />
        <input
          value={text}
          className={searchBarInputClasses}
          placeholder={placeholder}
          name={name}
          onChange={handleChange}
          disabled={disabled}
          autoComplete={"off"}
          data-testid={testId ? `test__search-input__${testId}` : undefined}
          onKeyDown={handleEnter}
          tabIndex={0}
          ref={inputRef}
          autoFocus={autoFocus}
          onFocus={() => {
            setInputFocused(true);
          }}
          onBlur={() => {
            setInputFocused(false);
          }}
        />
        {createClosingX()}
        {searchSuggestionsComponent}
      </div>
      {displaySearchButton && !open ? searchButton() : null}
    </div>
  );
}
